<template>
  <v-dialog v-model="isOpen" max-width="650px" @update:modelValue="onDialogToggle">
    <v-card>
      <v-card-title>Add Company</v-card-title>

      <!-- Result dialog — sits on top of the form -->
      <v-dialog v-model="resultDialog.show" max-width="420px">
        <v-card>
          <v-card-title>{{ resultDialog.success ? 'Company Provisioned' : 'Provisioning Failed' }}</v-card-title>
          <v-card-text style="white-space: pre-line;">{{ resultDialog.message }}</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="onResultOk">OK</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-card-text class="form-scroll">

        <!-- Company Name -->
        <v-text-field
          v-model="form.name"
          label="Company Name *"
          :error-messages="errors.name"
          :class="{ 'shake-animation': shakes.name }"
          @input="errors.name = ''"
        ></v-text-field>

        <!-- Environment -->
        <v-select
          v-model="form.environment"
          :items="environmentOptions"
          label="Environment"
        ></v-select>

        <!-- Area Code -->
        <v-text-field
          v-model="form.areaCode"
          label="Area Code *"
          maxlength="3"
          :error-messages="errors.areaCode"
          :class="{ 'shake-animation': shakes.areaCode }"
          @input="onAreaCodeInput"
        ></v-text-field>

        <!-- Timezone -->
        <v-select
          v-model="form.timezone"
          :items="timezoneOptions"
          label="Timezone"
        ></v-select>

        <!-- Intake Notification Emails -->
        <v-text-field
          v-model="form.intakeNotificationEmails"
          label="Intake Notification Emails"
        ></v-text-field>

        <!-- Forwarding Number -->
        <v-text-field
          v-model="form.forwardingNumber"
          label="Forwarding Number"
          :error-messages="errors.forwardingNumber"
          :class="{ 'shake-animation': shakes.forwardingNumber }"
          @input="errors.forwardingNumber = ''"
        ></v-text-field>

        <!-- SMS Notifications -->
        <v-text-field
          v-model="form.smsNotificationNumbers"
          label="SMS Notification Numbers"
          :error-messages="errors.smsNotificationNumbers"
          :class="{ 'shake-animation': shakes.smsNotificationNumbers }"
          @input="errors.smsNotificationNumbers = ''"
        ></v-text-field>

        <!-- Jail Call Forwarding -->
        <v-text-field
          v-model="form.jailCallForwardingNumber"
          label="Jail Call Forwarding Number"
          :error-messages="errors.jailCallForwardingNumber"
          :class="{ 'shake-animation': shakes.jailCallForwardingNumber }"
          @input="errors.jailCallForwardingNumber = ''"
        ></v-text-field>

        <!-- Voicemail Prompt -->
        <v-textarea
          v-model="form.voicemailPrompt"
          label="Voicemail Prompt (English) *"
          rows="4"
          :error-messages="errors.voicemailPrompt"
          :class="{ 'shake-animation': shakes.voicemailPrompt }"
          @input="errors.voicemailPrompt = ''"
        ></v-textarea>

        <!-- Language Support -->
        <div class="section-label">Language Support</div>
        <div class="language-checkboxes">
          <v-checkbox
            v-model="form.languageSupport['en-US']"
            label="English (en-US)"
            disabled
            color="#d2b48c"
            hide-details
          ></v-checkbox>
          <v-checkbox
            v-model="form.languageSupport['es-MX']"
            label="Spanish (es-MX)"
            color="#d2b48c"
            hide-details
          ></v-checkbox>
          <v-checkbox
            v-model="form.languageSupport['ru-RU']"
            label="Russian (ru-RU)"
            color="#d2b48c"
            hide-details
          ></v-checkbox>
        </div>

        <!-- Intake Menu JSON -->
        <v-textarea
          v-model="form.intakeMenu"
          label="Intake Menu (JSON)"
          rows="4"
          class="mt-4"
          placeholder="Paste JSON from Mongo"
        ></v-textarea>

        <!-- Billing -->
        <div class="section-label">Billing</div>
        <v-text-field
          v-model="form.billingPaymentType"
          label="Payment Type"
        ></v-text-field>
        <v-text-field
          v-model="form.billingDiscount"
          label="Discount"
        ></v-text-field>

        <!-- No Beep -->
        <v-switch
          v-model="form.nobeep"
          label="No Beep"
          color="#d2b48c"
          hide-details
          class="mb-4"
        ></v-switch>

        <!-- Icon Upload -->
        <v-file-input
          v-model="form.icon"
          label="Upload Company Icon"
          accept="image/*"
          clearable
        ></v-file-input>

      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="cancel" :disabled="submitting">Cancel</v-btn>
        <v-btn text color="primary" @click="submit" :loading="submitting">Add</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';

export default {
  name: 'OwnerAccountAddCompany',

  props: {
    modelValue: {
      type: Boolean,
      required: true
    }
  },

  emits: ['update:modelValue', 'saved'],

  data() {
    return {
      form: this.freshForm(),
      errors: {
        name: '',
        areaCode: '',
        voicemailPrompt: '',
        forwardingNumber: '',
        jailCallForwardingNumber: '',
        smsNotificationNumbers: ''
      },
      shakes: {
        name: false,
        areaCode: false,
        voicemailPrompt: false,
        forwardingNumber: false,
        jailCallForwardingNumber: false,
        smsNotificationNumbers: false
      },
      submitting: false,
      savedCompany: null,
      resultDialog: {
        show: false,
        success: false,
        message: ''
      },
      environmentOptions: ['production', 'test'],
      timezoneOptions: [
        'America/New_York',
        'America/Chicago',
        'America/Denver',
        'America/Los_Angeles',
        'America/Anchorage',
        'America/Honolulu'
      ]
    };
  },

  computed: {
    isOpen: {
      get() { return this.modelValue; },
      set(val) { this.$emit('update:modelValue', val); }
    }
  },

  methods: {
    freshForm() {
      return {
        name: '',
        environment: 'production',
        areaCode: '425',
        timezone: 'America/Los_Angeles',
        intakeNotificationEmails: '',
        forwardingNumber: '',
        smsNotificationNumbers: '',
        jailCallForwardingNumber: '',
        voicemailPrompt: '',
        languageSupport: {
          'en-US': true,
          'es-MX': false,
          'ru-RU': false
        },
        intakeMenu: '',
        billingPaymentType: 'INVOICE',
        billingDiscount: '',
        nobeep: true,
        icon: null
      };
    },

    onDialogToggle(val) {
      if (!val) this.resetForm();
    },

    resetForm() {
      this.form = this.freshForm();
      this.errors = { name: '', areaCode: '', voicemailPrompt: '', forwardingNumber: '', jailCallForwardingNumber: '', smsNotificationNumbers: '' };
      this.shakes = { name: false, areaCode: false, voicemailPrompt: false, forwardingNumber: false, jailCallForwardingNumber: false, smsNotificationNumbers: false };
      this.submitting = false;
      this.savedCompany = null;
    },

    cancel() {
      this.resetForm();
      this.isOpen = false;
    },

    // Called when user clicks OK on the result dialog
    onResultOk() {
      this.resultDialog.show = false;
      if (this.resultDialog.success) {
        // Success path: emit saved, reset, close the Add form
        this.$emit('saved', this.savedCompany);
        this.resetForm();
        this.isOpen = false;
      }
      // Failure path: just dismiss the result dialog, leave Add form open
    },

    onAreaCodeInput() {
      this.form.areaCode = this.form.areaCode.replace(/\D/g, '');
      this.errors.areaCode = '';
    },

    triggerShake(field) {
      this.shakes[field] = true;
      setTimeout(() => { this.shakes[field] = false; }, 500);
    },

    countDigits(str) {
      return (str.match(/\d/g) || []).length;
    },

    validate() {
      let valid = true;

      if (!this.form.name.trim()) {
        this.errors.name = 'Company name is required';
        this.triggerShake('name');
        valid = false;
      }

      if (!this.form.areaCode || this.form.areaCode.length !== 3) {
        this.errors.areaCode = 'Area code must be 3 digits';
        this.triggerShake('areaCode');
        valid = false;
      }

      if (!this.form.voicemailPrompt.trim()) {
        this.errors.voicemailPrompt = 'Voicemail prompt is required';
        this.triggerShake('voicemailPrompt');
        valid = false;
      }

      if (this.form.forwardingNumber.trim() && this.countDigits(this.form.forwardingNumber) !== 10) {
        this.errors.forwardingNumber = 'Must contain exactly 10 digits';
        this.triggerShake('forwardingNumber');
        valid = false;
      }

      if (this.form.jailCallForwardingNumber.trim() && this.countDigits(this.form.jailCallForwardingNumber) !== 10) {
        this.errors.jailCallForwardingNumber = 'Must contain exactly 10 digits';
        this.triggerShake('jailCallForwardingNumber');
        valid = false;
      }

      if (this.form.smsNotificationNumbers.trim()) {
        const entries = this.form.smsNotificationNumbers.split(',');
        const invalidEntries = entries.filter(e => e.trim() && this.countDigits(e) !== 10);
        if (invalidEntries.length > 0) {
          this.errors.smsNotificationNumbers = 'Each entry must contain exactly 10 digits';
          this.triggerShake('smsNotificationNumbers');
          valid = false;
        }
      }

      return valid;
    },

    async submit() {
      if (!this.validate()) return;

      this.submitting = true;

      try {
        // Step 1: Provision the three Twilio numbers
        const provisionResponse = await axios.post(
          `${process.env.VUE_APP_THIS_SERVER}/portaldb/db/provision/company`,
          {
            companyName: this.form.name.trim(),
            areaCode: this.form.areaCode
          }
        );

        if (!provisionResponse.data.success) {
          this.resultDialog = {
            show: true,
            success: false,
            message: provisionResponse.data.message || 'Provisioning failed.'
          };
          return;
        }

        const { bbasMainPhone, voicemailPhone, menuDirectPhone } = provisionResponse.data;

        // Step 2: Parse intakeMenu if provided
        let intakeMenu = null;
        if (this.form.intakeMenu.trim()) {
          try {
            intakeMenu = JSON.parse(this.form.intakeMenu.trim());
          } catch (e) {
            console.warn('intakeMenu JSON parse failed — storing null');
          }
        }

        // Step 3: Save the company to Mongo
        const formData = new FormData();
        formData.append('name', this.form.name.trim());
        formData.append('environment', this.form.environment);
        formData.append('areaCode', this.form.areaCode);
        formData.append('timezone', this.form.timezone);
        formData.append('intakeNotificationEmails', this.form.intakeNotificationEmails);
        formData.append('forwardingNumber', this.form.forwardingNumber);
        formData.append('smsNotificationNumbers', this.form.smsNotificationNumbers);
        formData.append('jailCallForwardingNumber', this.form.jailCallForwardingNumber);
        formData.append('voicemailPromptEn', this.form.voicemailPrompt.trim());
        formData.append('languageSupport', JSON.stringify(this.form.languageSupport));
        formData.append('intakeMenu', intakeMenu ? JSON.stringify(intakeMenu) : '');
        formData.append('billingPaymentType', this.form.billingPaymentType);
        formData.append('billingDiscount', this.form.billingDiscount);
        formData.append('nobeep', this.form.nobeep ? 'true' : 'false');
        formData.append('bbasMainPhone', bbasMainPhone);
        formData.append('voicemailPhone', voicemailPhone);
        formData.append('menuDirectPhone', menuDirectPhone);
        formData.append('nonJailCallHandling', 'voicemail');
        formData.append('jailCallHandling', 'normal');
        if (this.form.icon) {
          formData.append('icon', this.form.icon);
        }

        const saveResponse = await axios.post(
          `${process.env.VUE_APP_THIS_SERVER}/portaldb/db/companies`,
          formData
        );

        // Step 4: Store saved company, show success dialog
        // Form stays open until user clicks OK on the dialog
        this.savedCompany = saveResponse.data.company;
        this.resultDialog = {
          show: true,
          success: true,
          message: `MAIN SYSTEM PHONE: ${bbasMainPhone}\nVOICEMAIL: ${voicemailPhone}\nMENU: ${menuDirectPhone}`
        };

      } catch (error) {
        console.error('Error adding company:', error);
        this.resultDialog = {
          show: true,
          success: false,
          message: error.response?.data?.message || error.message || 'An unexpected error occurred.'
        };
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.form-scroll {
  max-height: 65vh;
  overflow-y: auto;
}

.section-label {
  font-size: 13px;
  color: #888;
  margin-bottom: 4px;
  margin-top: 8px;
}

.language-checkboxes {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.shake-animation {
  animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both;
}

@keyframes shake {
  10%, 90% { transform: translate3d(-1px, 0, 0); }
  20%, 80% { transform: translate3d(2px, 0, 0); }
  30%, 50%, 70% { transform: translate3d(-4px, 0, 0); }
  40%, 60% { transform: translate3d(4px, 0, 0); }
}
</style>
